import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import InfoIcon from '~/assets/images/info-icon.svg';
import CTooltip from '~/components/CommonTooltip';
import CopyWithTooltip from '~/components/CopyWithTooltip';
import useResponsive from '~/hooks/useResponsive';

import UsageChart from '../../../components/UsageChart';

interface MetricProps {
  label: string;
  displayValue?: ReactNode;
  displayValueColor?: string;
  showCopyIcon?: boolean;
  percentage?: number | null;
  used?: string | number | null;
  total?: string | number | null;
  threshold?: number;
  canShow?: boolean;
  isAdvanced: boolean;
  tooltipMessage?: string;
}

const Metric = ({
  label,
  displayValue = undefined,
  displayValueColor = undefined,
  showCopyIcon = false,
  percentage = null,
  used = null,
  total = null,
  threshold = undefined,
  canShow = true,
  isAdvanced,
  tooltipMessage = undefined,
}: MetricProps) => {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();

  if (!canShow) {
    return null;
  }

  const infoIconSize = 24;

  return (
    <Card
      sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      data-testid="metric-item"
    >
      <Stack spacing={isAdvanced ? 3 : 1.25} flex={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={'center'}
        >
          <Typography variant="h3">{label}</Typography>
          {tooltipMessage && (
            <CTooltip
              title={tooltipMessage}
              arrow
              placement="top-start"
              data-testid="info-list-item-tooltip"
            >
              <IconButton size="medium">
                <Box
                  component="img"
                  src={InfoIcon}
                  alt={t('infoIcon.altText') as string}
                  width={infoIconSize}
                  height={infoIconSize}
                />
              </IconButton>
            </CTooltip>
          )}
        </Stack>
        {displayValue && (
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Typography variant="body2" color={displayValueColor}>
              {displayValue}
            </Typography>
            {showCopyIcon && <CopyWithTooltip text={displayValue as string} />}
          </Stack>
        )}
      </Stack>
      {percentage != null && (
        <UsageChart
          used={used}
          total={total}
          percentage={percentage}
          threshold={threshold}
          variant={isMobile() ? 'default' : 'compact'}
        />
      )}
    </Card>
  );
};

interface MetricsProps {
  items: MetricProps[];
}

const Metrics = ({ items }: MetricsProps) => {
  const visibleItems = items.filter((item) => item.canShow);
  const numOfCard = visibleItems.length;
  if (numOfCard === 0) return null;

  const mdValue = Math.max(12 / Math.min(numOfCard, 4), 3);

  return (
    <Grid container spacing={2} sx={{ mt: 2 }} data-testid="metrics-container">
      {visibleItems?.map(
        (
          {
            label,
            displayValue,
            displayValueColor,
            showCopyIcon,
            used,
            total,
            percentage,
            threshold,
            isAdvanced,
            tooltipMessage,
          },
          index,
        ) => (
          <Grid item xs={12} md={mdValue} key={`${label}-${index}`}>
            <Metric
              label={label}
              displayValue={displayValue}
              displayValueColor={displayValueColor}
              showCopyIcon={showCopyIcon}
              used={used}
              total={total}
              percentage={percentage}
              threshold={threshold}
              isAdvanced={isAdvanced}
              tooltipMessage={tooltipMessage}
            />
          </Grid>
        ),
      )}
    </Grid>
  );
};

export default Metrics;
